import React, { useEffect, useState } from 'react';
import StepIndicator from '../../organisms/step-indicator/StepIndicator';
import Screen from '../../organisms/screen/Screen';
import Loader from '../../organisms/loader/Loader';
import WalkthroughPlayerV2 from './v2/WalkthroughPlayerV2';
import WalkthroughSpotlight from './v2/WalkthroughSpotlight';
import { getValue } from '../utils/tools';
import { useDispatch, useSelector } from 'react-redux';
import {
  getSteps,
  setIndex,
  getIndex, getConfig,
  getLoading,
} from '../../../redux/walkthroughPreviewSlice';
import FontOption from '../../organisms/editor-design/options/FontOption';
import { getEmbedConfig, getEmbedIndex, getEmbedLoading, getEmbedSteps, setEmbedIndex } from '../../../redux/walkthroughEmbedPreviewSlice';
import { HUB_ENDPOINTS } from '../utils/endpoints';
import { getMaterial } from '../../../redux/materialSlice';
import { http_get, http_post } from '../utils/hub_client';
import { setResponseActiveStep, getAudienceData, getCompanyDetails } from '../../../redux/hubSlice';
import { getCompany } from '../../../redux/companySlice';

// New learner design (mockup 07). ON for local + staging so the published Hub
// view + editor Preview both adopt it for review; OFF on production domains
// until we deliberately roll out. Broadened from a strict localhost match so it
// works regardless of the exact local host (e.g. a company subdomain on
// localhost, lvh.me, or Heroku staging).
const USE_LEARNER_V2 = (() => {
  if (typeof window === 'undefined' || !window.location) return false;
  // Server-driven cutover switch (BL-146 rollout). The layouts render
  //   <meta name="learner-v2" content="true|false">
  // from ENV['LEARNER_V2_ENABLED'], so production can be flipped ON — or rolled
  // back instantly — by changing the env var and restarting, with no rebuild.
  const meta = typeof document !== 'undefined' && document.querySelector('meta[name="learner-v2"]');
  if (meta) return meta.getAttribute('content') !== 'false';
  // Fallback when no meta is present: previous host-based gating (local +
  // staging on, production off) — keeps behavior identical to before.
  const h = window.location.hostname || '';
  return /localhost|127\.0\.0\.1|\blvh\.me|herokuapp\.com/.test(h) || h.endsWith('.local');
})();

const StepsViewer = ({ embed = false, id }) => {
  const dispatch = useDispatch();
  const steps = useSelector((state) => embed ? getEmbedSteps(state, id) : getSteps(state));
  const index = useSelector((state) => embed ? getEmbedIndex(state, id) : getIndex(state));
  const pageConfig = useSelector((state) => embed ? getEmbedConfig(state, id) : getConfig(state));
  const isLoading = useSelector((state) => embed ? getEmbedLoading(state, id) : getLoading(state));
  const material = useSelector((state) => getMaterial(state));
  const editorCompany = useSelector((state) => getCompany(state));
  const hubCompany = useSelector((state) => getCompanyDetails(state));
  const audience = useSelector((state) => getAudienceData(state));
  const company = (editorCompany && editorCompany.slug) ? editorCompany : hubCompany;

  const [activeStep, setActiveStep] = useState();
  const [currentIndex, setCurrentIndex] = useState(0);
  const getResponse = useSelector((state) => state.hub.responseData.completeStatus);

  useEffect(() => {
    const hubUrl = window.location.pathname.split('/')[2];
    if (hubUrl == 'hub' && material.id) {
      fetchResponse();
    }
    if (steps.length > 0) {
      setActiveStep(steps[0]);
      setCurrentIndex(0);
    }
  }, [steps, material.id]);

  const handleNextStep = async () => {
    const hubUrl = window.location.pathname.split('/')[2];
    if (hubUrl == 'hub') {
      await saveResponse();
    }
    if (currentIndex < steps.length - 1) {
      const nextStep = steps[currentIndex + 1];
      handleStepChange(nextStep, currentIndex + 1);
    }
  }
  
  const handleStepChange = async (_step, index) => {
    if (index === undefined) return;
    setActiveStep(steps[index]);
    setCurrentIndex(index);
    dispatch(embed ? setEmbedIndex({ data: index, id: id }) : setIndex(index));
  };

  const fetchResponse = async () => {
    const url = HUB_ENDPOINTS.GET_RESPONSE(material.id);
    try {
      const res = await http_get(url);
      const responseData = {
        minutes: res.data.data.minutes,
        response: res.data.data.hasOwnProperty('response') ? res.data.data.response : null
      };
      const completeStatus = responseData.response.reduce((acc, current) => {
        acc[current.id] = current.completion;
        return acc;
      }, {});
      dispatch(setResponseActiveStep({ ...responseData, completeStatus }));
      return responseData;
    } catch (error) {
      console.error("Error :", error);
    }
  }

  const saveResponse = async () => {

    const url = HUB_ENDPOINTS.SAVE_RESPONSE(material.id);
    const previousData = await fetchResponse();

    let newData;
    if (previousData && previousData.hasOwnProperty('response')) {
      newData = [...previousData.response]; // Create a copy of the previous response
      const existingIndex = newData.findIndex((e) => e.id === activeStep.id);
      if (existingIndex !== -1) {
        newData[existingIndex] = { "id": activeStep.id, "completion": true };
      } else {
        newData.push({ "id": activeStep.id, "completion": true });
      }
    } else {
      newData = [{ "id": activeStep.id, "completion": true }];
    }

    const newResponse = {
      "minutes": previousData ? previousData.minutes : 0,
      "response": newData
    }

    try {
      const res = await http_post(url, newResponse);
      const materials = res.data.data;
      dispatch(setResponseActiveStep({ ...materials }));
      const updatedGetResponse = { ...getResponse, [activeStep.id]: true };
      dispatch(setResponseActiveStep({ ...materials, completeStatus: updatedGetResponse }));
    } catch (error) {
      console.error("Error completing step:", error);
    }
  }

  const defaultColors = {
    primary: '#3BA4B4',
    background: '#F6F8F9',
    text: '#363841',
    btnColor: '#3BA4B4',
    btnTextColor: '#FFFFFF',
    config: pageConfig
  };

  const style = {
    backgroundColor: defaultColors.background,
    color: defaultColors.text
  };

  const saveCurrentStep = async (current_step) => {
    if(!material.id) return;
    const url = HUB_ENDPOINTS.SAVE_CURRENT_STEP(material.id);
    try {
      const res = await http_post(url, {
        current_step
      });
      const result = res.data.data;
    } catch (error) {
      console.error("Error saving current step:", error);
    }
  }

  useEffect(() => {
    const hubUrl = window.location.pathname.split('/')[2];
    if (hubUrl == 'hub' && material){
      saveCurrentStep(currentIndex + 1)
    }
  }, [activeStep, material])


  const useV2 = USE_LEARNER_V2 && !embed;
  const playbackStyle = getValue(defaultColors, 'pb', 'pbStyle') || 'default';
  const PlayerComp = playbackStyle === 'spotlight' ? WalkthroughSpotlight : WalkthroughPlayerV2;
  // Full-page render: the dedicated published page and its full_view preview
  // (compact — screenshot ~60%, footer visible without scrolling). The
  // in-editor Preview keeps the larger default size.
  const fullPage = (() => {
    if (typeof window === 'undefined' || !window.location) return false;
    const path = window.location.pathname || '';
    return /\/full_view\/?$/.test(path) || path.split('/')[2] === 'hub';
  })();

  return (
    <React.Fragment>
      {isLoading && <Loader />}

      {!isLoading && useV2 && (
        <PlayerComp
          title={material?.name}
          steps={steps}
          index={index}
          configColors={defaultColors}
          onNext={() => handleNextStep()}
          onPrev={() => handleStepChange(activeStep, Math.max(0, currentIndex - 1))}
          onGoto={(step, i) => handleStepChange(step, i)}
          onChange={() => handleNextStep()}
          embed={embed}
          id={id}
          showNav={typeof window !== 'undefined' && (window.location.pathname.split('/')[2] !== 'hub')}
          brandName={(company && company.name) || material?.company_name}
          companySlug={(company && company.slug) || material?.company_slug}
          learner={audience && audience.email ? audience : null}
          fullPage={fullPage}
        />
      )}

      {!isLoading && !useV2 && (
        <div style={style}>
          <StepIndicator
            index={index}
            steps={steps}
            onChange={(index) => handleStepChange(activeStep, index)}
            configColors={defaultColors}
          />
          <Screen
            onChange={() => handleNextStep()}
            onActionBtn={(index) => handleStepChange(activeStep, index)}
            configColors={defaultColors}
            steps={steps}
            index={index}
            embed={embed}
            id={id}
          />
          <div hidden>
            <FontOption />
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

export default StepsViewer;
